import { HttpClient } from '@angular/common/http';
import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { DataTableUtilities } from '@app/core/utilities/data-table-utilities';
import { BasePairsPipe } from '@app/core/utilities/pipes/base-pairs-format-pipe';
import { BytesPipe } from '@app/core/utilities/pipes/bytes.pipe';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { ProgressComponent } from '@bssh/comp-lib';
import { BasespaceService } from '@bssh/ng-sdk';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ColDef, GridOptions, ICellRendererParams } from 'ag-grid-community';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { SubSink } from 'subsink';

export interface BiosampleLinkRendererBaseParams {
  linkText?: string;
  linkUrl?: string;
  tooltipText?: string;
  target?: '_blank' | '_self';
  hasPopover?: boolean;
  biosampleId?: string;
}

export interface BiosampleLinkRendererParams extends ICellRendererParams, BiosampleLinkRendererBaseParams {}

@Component({
  selector: 'app-biosample-link-renderer',
  templateUrl: './biosample-link-renderer.component.html',
  styleUrls: ['./biosample-link-renderer.component.scss']
})
export class BiosampleLinkRendererComponent implements OnInit, OnDestroy, ICellRendererAngularComp {
  @ViewChild('popoverLoadingSpinner', { static: false }) public popoverLoadingSpinner: ProgressComponent;

  public params: BiosampleLinkRendererParams;
  public linkText: string;
  public linkUrl: string;
  public tooltipText: string;
  public target: string = '_blank';
  public hasTooltip: boolean = false;
  public hasLink: boolean = false;
  public hasPopover: boolean = true;
  public linkStyle: string;
  public biosampleId: string;
  public datasetsTableData: any[] = [];
  public columnDefs: ColDef[] = [];
  public gridOptions: GridOptions;
  public isLoading$ = new BehaviorSubject<boolean>(false);
  public actualYield: string = '--';

  private subs: SubSink = new SubSink();

  public datasetsResponse: any;
  public biosampleYieldResponse: any;

  get tooltipPosition(): string {
    if(!this.params || this.params.node.rowIndex !== 0) {
      return 'top';
    } else {
      return 'right';
    }
  }

  @HostBinding('style.--column-width') 
  get columnWidth(): string {
    if(this.params) {
      return `${this.params.column.getActualWidth() - 80}px`;
    }
  }

  constructor(
    private http: HttpClient,
    private basespaceApi: BasespaceService,
    private bytesPipe: BytesPipe,
    private basePairsPipe: BasePairsPipe,
  ) { }

  ngOnInit() {
    this.linkStyle = `
      .rad-btn--link-container {
        overflow: hidden;
        display: inline-block;
        vertical-align: middle;
        text-overflow: ellipsis;
        width: auto;
        max-width: var(--column-width, 80px);
      }
    `;
    this.initializeTableColumns();
  }

  private initializeTableColumns(): void {
    this.columnDefs = [
      {
        headerName: 'Dataset Name',
        field: 'Name',
        cellRendererFramework: RadLinkRendererComponent,
        cellRendererParams: (params) => {
          if (params.data && params.data.AppSession && params.data.Id) {
            const datasetUrl = `/analyses/${params.data.AppSession.Id}/files/${params.data.Id}`;
            return DataTableUtilities.getRadLinkParams(
              params.data.Name || '--',
              datasetUrl
            );
          }
          return DataTableUtilities.getRadLinkParams(params.data.Name || '--');
        },
        flex: 3,
      },
      {
        headerName: 'Library',
        valueGetter: (params) => {
          if (!params.data || !params.data.Properties || !params.data.Properties.Items) {
            return '--';
          }
          const librariesProperty = params.data.Properties.Items.find(
            (prop: any) => prop.Name === 'Input.Libraries'
          );
          if (librariesProperty && 
              librariesProperty.SampleLibraryItems && 
              librariesProperty.SampleLibraryItems.length > 0) {
            return librariesProperty.SampleLibraryItems[0].Name || '--';
          }
          return '--';
        },
        cellRendererFramework: RadLinkRendererComponent,
        cellRendererParams: (params) => {
          return DataTableUtilities.getRadLinkParams(params.value || '--');
        },
        flex: 2,
      },
      {
        headerName: 'Run',
        valueGetter: (params) => {
          if (!params.data || !params.data.Properties || !params.data.Properties.Items) {
            return null;
          }
          const runsProperty = params.data.Properties.Items.find((prop: any) => prop.Name === 'Input.Runs');
          if (
            runsProperty && 
            runsProperty.RunItems && 
            runsProperty.RunItems.length > 0) {
            return runsProperty.RunItems[0];
          }
          return null;
        },
        cellRendererFramework: RadLinkRendererComponent,
        cellRendererParams: (params) => {
          return DataTableUtilities.getRadLinkParams(
            params.value ? params.value.Name : '--',
            params.value ? BsApiEndPoints.getRunDetailsRoute(params.value.Id) : null
          );
        },
        flex: 2,
      },
      {
        headerName: 'Lane',
        field: 'Lane',
        valueGetter: (params) => {
          return params.data.Lane || '--';
        },
        cellRendererFramework: RadLinkRendererComponent,
        cellRendererParams: (params) => {
          return DataTableUtilities.getRadLinkParams(params.value || '--');
        },
        flex: 1,
      },
      {
        headerName: 'Size',
        field: 'TotalSize',
        valueGetter: (params) => {
          if (!params.data || !params.data.TotalSize) {
            return '--';
          }
          return this.bytesPipe.transform(params.data.TotalSize, 2);
        },
        cellRendererFramework: RadLinkRendererComponent,
        cellRendererParams: (params) => {
          return DataTableUtilities.getRadLinkParams(params.value || '--');
        },
        flex: 1,
      }
    ];

    this.gridOptions = {
      columnDefs: this.columnDefs,
      domLayout: 'autoHeight',
      suppressHorizontalScroll: true,
      suppressRowClickSelection: true,
      rowSelection: 'none',
      defaultColDef: {
        sortable: false,
        filter: false,
        resizable: false,
      }
    };
  }

  agInit(params: BiosampleLinkRendererParams): void {
    this.params = params;
    
    // Set link text - use provided text, cell value, or fallback
    this.linkText = params.linkText || params.value || '';
    this.linkUrl = params.linkUrl;
    this.hasLink = !!params.linkUrl
    this.biosampleId = params.biosampleId;
    
    // Set tooltip text if provided
    this.tooltipText = params.tooltipText;
    this.hasTooltip = !!this.tooltipText;
    
    // Set target
    this.target = params.target || '_blank';
    
    // Set popover visibility
    this.hasPopover = params.hasPopover || true;
  }

  refresh(params: BiosampleLinkRendererParams): boolean {
    this.agInit(params);

    return true;
  }

  public onPopoverShown(): void {
    if(!this.datasetsResponse && !this.biosampleYieldResponse) {
      this.isLoading$.next(true);

      // Need to add delay to wait for spinner to get initialized in component
      setTimeout(() => {
        if (this.popoverLoadingSpinner) {
          this.popoverLoadingSpinner.start();
        }
      }, 0);

      const datasets$ = this.http.get<any>(BsApiEndPoints.V2_DATASETS, {
        params: {
          DatasetTypes: '~common.fastq',
          Include: 'properties',
          InputBiosamples: this.biosampleId,
          IsArchived: 'false',
          PropertyFilters: 'Input.Libraries,Input.Runs,BaseSpace.Metrics.FastQ&QcStatus=QcPassed,Undefined',
          limit: '10',
          offset: '0',
          sortBy: 'DateCreated',
          sortDir: 'desc',
        },
        withCredentials: true
      });
      const biosampleYield$ = this.basespaceApi.GetV2BiosamplesId({ id: this.biosampleId, include: ['Yields']});

      this.subs.sink = forkJoin(datasets$, biosampleYield$)
        .subscribe(([datasetsResponse, biosampleYieldResponse]) => {
          this.datasetsResponse = this.processDatasetsResponse(datasetsResponse);
          this.biosampleYieldResponse = this.processBiosampleYieldResponse(biosampleYieldResponse);

          this.isLoading$.next(false);
          if (this.popoverLoadingSpinner) {
            this.popoverLoadingSpinner.complete();
          }
      });
    }
  }

  public onPopoverClick(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
  }

  private processDatasetsResponse(response: any): void {
    if (!response || !response.Items) {
      this.datasetsTableData = [];
      return;
    }

    // Pass raw dataset items directly - column definitions handle the data manipulation
    this.datasetsTableData = response.Items;
  }

  private processBiosampleYieldResponse(response: any): void {
    if (!response || !response.Yields || !response.Yields.length) {
      this.actualYield = '--';
      return;
    }

    // Sum all TotalActualYield values from all yields
    const totalActualYield = response.Yields.reduce((sum: number, yieldItem: any) => {
      const yieldValue = yieldItem.TotalActualYield;
      return sum + (yieldValue !== null && yieldValue !== undefined ? yieldValue : 0);
    }, 0);

    if (totalActualYield > 0) {
      this.actualYield = this.basePairsPipe.transform(totalActualYield, 2);
    } else {
      this.actualYield = '--';
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
